import { useState, useEffect, useCallback, useDeferredValue, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { SolutionCardProps } from '@/components/SolutionCard';
import { toast } from '@/components/ui/use-toast';
import { TagCategoryMappingService, type ExtendedSolution } from '@/services/tagCategoryMappingService';

// Define the fixed AI categories (display names only - IDs will come from DB)
const AI_CATEGORY_NAMES = [
  'Language',
  'Image',
  'Video',
  'Audio',
  'Multimodal',
  'Other'
];

interface Category {
  id: string;
  name: string;
  count: number;
}

interface SolutionFilteringData {
  categories: Category[];
  solutions: ExtendedSolution[];
  mappingService: TagCategoryMappingService;
}

interface SolutionFilteringResult {
  // Data
  categories: Category[];
  filteredSolutions: SolutionCardProps[];
  totalSolutions: number;
  
  // State
  selectedCategory: string | null;
  searchQuery: string;
  isLoading: boolean;
  isFiltering: boolean;
  isError: boolean;
  filterError: Error | null;
  
  // Actions
  setSelectedCategory: (categoryId: string | null) => void;
  setSearchQuery: (query: string) => void;
  refetch: () => void;
  clearFilters: () => void;
  
  // Utilities
  getCategoryName: (categoryId: string | null) => string | null;
}

// Enhanced fetch function using TagCategoryMappingService
const fetchSolutionsWithEnhancedFiltering = async (): Promise<SolutionFilteringData> => {
  try {
    // Initialize the mapping service
    const mappingService = TagCategoryMappingService.getInstance();
    
    // Fetch categories from database
    const { data: categoriesData, error: categoriesError } = await supabase
      .from('categories')
      .select('id, name, count')
      .order('name');
      
    if (categoriesError) {
      console.error('Error fetching categories:', categoriesError);
      throw categoriesError;
    }
    
    // Create our fixed categories with the DB UUIDs
    const categories = AI_CATEGORY_NAMES.map(name => {
      const category = categoriesData.find(c => c.name === name);
      return {
        id: category?.id || '',
        name,
        count: category?.count || 0
      };
    });
    
    // Fetch solutions with enhanced data for tag-based filtering
    const { data: solutionsData, error: solutionsError } = await supabase
      .from('solutions')
      .select(`
        id, 
        name, 
        short_description,
        description,
        details,
        featured, 
        image_url,
        created_at,
        updated_at,
        status,
        slug,
        solution_categories(categories:category_id(name)),
        solution_tags(tags:tag_id(name))
      `)
      .or('status.is.null,status.neq.classified')
      .order('name');
      
    if (solutionsError) {
      console.error('Error fetching solutions:', solutionsError);
      throw solutionsError;
    }
    
    // Transform data to match ExtendedSolution interface
    const solutions: ExtendedSolution[] = solutionsData.map((solution) => ({
      id: solution.id,
      name: solution.name,
      short_description: solution.short_description,
      description: solution.description,
      details: solution.details,
      image_url: solution.image_url,
      featured: solution.featured,
      created_at: solution.created_at,
      updated_at: solution.updated_at,
      status: solution.status,
      slug: solution.slug,
      tags: solution.solution_tags?.map((tagRel: any) => tagRel.tags?.name).filter(Boolean) || [],
      categories: solution.solution_categories?.map((catRel: any) => catRel.categories?.name).filter(Boolean) || []
    }));
    
        // Calculate counts for each category efficiently using optimized lookups
    const allMappings = await mappingService.getMappings();
    
    // Create efficient lookups for tag-to-category relationships
    const tagToCategoriesMap = new Map<string, Set<string>>();
    allMappings.forEach(mapping => {
      const tagLower = mapping.tag_name.toLowerCase();
      if (!tagToCategoriesMap.has(tagLower)) {
        tagToCategoriesMap.set(tagLower, new Set());
      }
      tagToCategoriesMap.get(tagLower)!.add(mapping.category_name);
    });
    
    // Calculate counts for each category efficiently
    const categoriesWithCounts = categories.map(category => {
      if (category.name === 'Other') {
        // Special handling for "Other" category
        // Include solutions that don't match ANY other category
        const count = solutions.filter(solution => {
          // First check if solution matches any other category (excluding "Other")
          const matchesOtherCategory = categories
            .filter(cat => cat.name !== 'Other')
            .some(otherCategory => {
              // Check primary category
              if (solution.categories && solution.categories.includes(otherCategory.name)) {
                return true;
              }
              
              // Check tags using the optimized lookup
              if (solution.tags) {
                return solution.tags.some(tag => {
                  const tagCategories = tagToCategoriesMap.get(tag.toLowerCase());
                  return tagCategories && tagCategories.has(otherCategory.name);
                });
              }
              
              return false;
            });
          
          // If it doesn't match any other category, include it in "Other"
          if (!matchesOtherCategory) {
            return true;
          }
          
          // Also include if explicitly categorized as "Other"
          if (solution.categories && solution.categories.includes('Other')) {
            return true;
          }
          
          // Also include if has tags that map to "Other"
          if (solution.tags) {
            return solution.tags.some(tag => {
              const tagCategories = tagToCategoriesMap.get(tag.toLowerCase());
              return tagCategories && tagCategories.has('Other');
            });
          }
          
          return false;
        }).length;
        
        return {
          ...category,
          count
        };
      } else {
        // Regular category filtering logic
        const count = solutions.filter(solution => {
          // Check primary category
          if (solution.categories && solution.categories.includes(category.name)) {
            return true;
          }
          
          // Check tags using the optimized lookup
          if (solution.tags) {
            return solution.tags.some(tag => {
              const tagCategories = tagToCategoriesMap.get(tag.toLowerCase());
              return tagCategories && tagCategories.has(category.name);
            });
          }
          
          return false;
        }).length;
        
        return {
          ...category,
          count
        };
      }
    });
    
    return {
      categories: categoriesWithCounts,
      solutions,
      mappingService
    };
  } catch (error) {
    console.error('Error in fetchSolutionsWithEnhancedFiltering:', error);
    toast({
      title: "Error loading solutions",
      description: "Could not load AI solutions. Please try again later.",
      variant: "destructive"
    });
    
    // Return empty data on error so the UI can display a helpful message
    return {
      categories: AI_CATEGORY_NAMES.map(name => ({ id: '', name, count: 0 })),
      solutions: [],
      mappingService: TagCategoryMappingService.getInstance()
    };
  }
};

// Custom debounce hook
const useDebounce = <T>(value: T, delay: number): T => {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
};

export const useSolutionFiltering = (queryKey: string = 'solution-filtering'): SolutionFilteringResult => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Use deferred values to prevent blocking during heavy computations
  const deferredSearchQuery = useDeferredValue(searchQuery);
  const deferredCategory = useDeferredValue(selectedCategory);
  
  // Data fetching
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [queryKey],
    queryFn: fetchSolutionsWithEnhancedFiltering,
    refetchOnWindowFocus: false,
  });

  // Helper to map category IDs to names
  const getCategoryName = (categoryId: string | null): string | null => {
    if (!categoryId || !data?.categories) return null;
    const category = data.categories.find(c => c.id === categoryId);
    return category?.name || null;
  };

  // Optimize filtering with time slicing to prevent blocking main thread
  const filteredSolutions = useMemo(() => {
    if (!data) return [];
    
    const solutions = data.solutions;
    const batchSize = 50; // Process 50 items at a time
    const results: SolutionCardProps[] = [];
    
    // Time-sliced processing to prevent blocking
    const processBatch = (startIndex: number): SolutionCardProps[] => {
        const endIndex = Math.min(startIndex + batchSize, solutions.length);
        const batchResults: SolutionCardProps[] = [];
        
        for (let i = startIndex; i < endIndex; i++) {
          const solution = solutions[i];
          
          // Category filtering using deferred value - use the service method
          if (deferredCategory) {
            // Find the category name from the ID
            const categoryName = getCategoryName(deferredCategory);
            if (categoryName) {
              // Use the service to filter - this is async but we'll handle it synchronously for now
              // For performance, we'll do a simple check here instead of calling the async method
              const hasMatchingCategory = solution.categories?.includes(categoryName) || false;
              const hasMatchingTags = solution.tags?.some(tag => {
                // Simple tag matching - this could be enhanced with the service mapping
                return tag.toLowerCase().includes(categoryName.toLowerCase());
              }) || false;
              
              if (!hasMatchingCategory && !hasMatchingTags) continue;
            }
          }
          
          // Search filtering using deferred value
          if (deferredSearchQuery.trim()) {
            const query = deferredSearchQuery.toLowerCase().trim();
            const matchesSearch = 
              solution.name.toLowerCase().includes(query) ||
              solution.short_description?.toLowerCase().includes(query) ||
              solution.description?.toLowerCase().includes(query) ||
              solution.tags?.some(tag => tag.toLowerCase().includes(query)) ||
              solution.categories?.some(cat => cat.toLowerCase().includes(query));
            
            if (!matchesSearch) continue;
          }
          
          batchResults.push({
            id: solution.id,
            name: solution.name,
            category: solution.categories?.[0] || 'Other',
            shortDescription: solution.short_description,
            image: solution.image_url,
            featured: solution.featured || false,
            tags: solution.tags || [],
            slug: solution.slug
          });
        }
        
        return batchResults;
      };
      
    // Process all solutions in batches
    for (let i = 0; i < solutions.length; i += batchSize) {
      const batchResults = processBatch(i);
      results.push(...batchResults);
    }
    
    return results;
  }, [data, deferredCategory, deferredSearchQuery]);

  // Utility functions
  const clearFilters = () => {
    setSelectedCategory(null);
    setSearchQuery('');
    // setFilterError(null); // This line was removed from the new_code, so it's removed here.
  };

  return {
    // Data
    categories: data?.categories || [],
    filteredSolutions,
    totalSolutions: data?.solutions?.length || 0,
    
    // State
    selectedCategory,
    searchQuery,
    isLoading,
    isFiltering: false, // No longer doing client-side filtering async
    isError,
    filterError: null, // No longer tracking filter-specific errors
    
    // Actions
    setSelectedCategory,
    setSearchQuery,
    refetch,
    clearFilters,
    
    // Utilities
    getCategoryName
  };
};

// ============================================================================
// TRENDING & NEW SOLUTIONS FUNCTIONS
// ============================================================================

/**
 * Shared utility function to handle Supabase query results with consistent error handling
 */
const handleSupabaseResult = <T>(
  data: T | null,
  error: any,
  operationName: string
): T[] => {
  if (error) {
    console.error(`Error in ${operationName}:`, error);
    throw error;
  }

  return (data as T[]) || [];
};

/**
 * Get trending solutions for a specific time period based on actual click analytics
 */
export const getTrendingSolutions = async (timeframe: 'week' | 'month', limit: number = 3) => {
  try {
    const daysAgo = timeframe === 'week' ? 7 : 30;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - daysAgo);

    // Get trending solutions based on click data within the timeframe
    const { data, error } = await supabase
      .from('solution_clicks')
      .select(`
        solution_id,
        solutions!inner(
          id,
          name,
          short_description,
          image_url,
          featured,
          slug,
          solution_categories(categories(name)),
          solution_tags(tags(name))
        )
      `)
      .gte('clicked_at', cutoffDate.toISOString())
      .is('solutions.status', null)
      .order('clicked_at', { ascending: false });

    if (error) {
      console.error(`Error fetching trending data for ${timeframe}:`, error);
      throw error;
    }

    if (!data || data.length === 0) {
      console.log(`No click data found for ${timeframe}`);
      return [];
    }

    // Count clicks per solution and rank by popularity
    const clickCounts = new Map<string, { solution: any; clicks: number }>();
    
    data.forEach((record: any) => {
      const solution = record.solutions;
      if (solution && solution.id) {
        const existing = clickCounts.get(solution.id);
        if (existing) {
          existing.clicks++;
        } else {
          clickCounts.set(solution.id, { solution, clicks: 1 });
        }
      }
    });

    // Sort by click count and take top solutions
    const sortedSolutions = Array.from(clickCounts.values())
      .sort((a, b) => b.clicks - a.clicks)
      .slice(0, limit)
      .map(item => item.solution);

    console.log(`Found ${sortedSolutions.length} trending solutions for ${timeframe} with click data`);
    return sortedSolutions;

  } catch (error) {
    console.error(`Error executing getTrendingSolutions (${timeframe}):`, error);
    throw error;
  }
};

/**
 * Get newest solutions (by created_at)
 */
export const getNewSolutions = async (limit: number = 3) => {
  try {
    const { data, error } = await supabase
      .from('solutions')
      .select(`
        id,
        name,
        short_description,
        image_url,
        featured,
        slug,
        solution_categories(categories(name)),
        solution_tags(tags(name))
      `)
      .or('status.is.null,status.neq.classified')
      .order('created_at', { ascending: false })
      .limit(limit);

    return handleSupabaseResult(data, error, 'getNewSolutions');
  } catch (error) {
    console.error('Error executing getNewSolutions:', error);
    throw error;
  }
}; 